import { PERMISSION_DENIED_MESSAGE } from '../constants/permissions';
import {
  normalizeApiPath,
  isPublicApiRoute,
  resolveRequiredPermission,
  extractPermissionKeys,
  hasAnyPermission,
  shouldBypassCreateJobPermissionForRecruitmentRequest,
  resolveRecruitmentRequestIdForJobPermission,
  permissionGateShouldEnforce,
} from '../core';
import { maybeOpenPermissionDenied } from '../actions/permissionDenied';

export const PERMISSION_DENIED_CODE = 'PERMISSION_DENIED';

function parseArgs(args) {
  if (typeof args === 'string') return { url: args, method: 'GET', body: undefined };
  return {
    url: args?.url ?? '',
    method: (args?.method || 'GET').toUpperCase(),
    body: args?.body,
  };
}

const deny = (permissions) => {
  const blockedPermissions = Array.isArray(permissions)
    ? permissions
    : permissions
      ? [permissions]
      : [];
  return {
    error: {
      status: 403,
      data: {
        message: PERMISSION_DENIED_MESSAGE,
        code: PERMISSION_DENIED_CODE,
        requiredPermission: blockedPermissions[0],
        blockedPermissions,
      },
    },
  };
};

/** Wraps RTK Query fetchBaseQuery: permission check runs before network. */
export function createPermissionAwareBaseQuery(baseQuery) {
  return async (args, api, extra) => {
    const { url, method, body } = parseArgs(args);
    const path = normalizeApiPath(url);

    if (!path || isPublicApiRoute(method, url)) {
      return baseQuery(args, api, extra);
    }

    const rule = resolveRequiredPermission(method, path);
    if (!rule) return baseQuery(args, api, extra);

    const recruitmentRequestId = resolveRecruitmentRequestIdForJobPermission(
      api.getState(),
      body,
      path,
      url,
    );

    if (
      shouldBypassCreateJobPermissionForRecruitmentRequest(
        recruitmentRequestId,
        rule.permission,
      )
    ) {
      return baseQuery(args, api, extra);
    }

    const account = api.getState()?.auth?.account;

    if (!permissionGateShouldEnforce(account)) {
      return baseQuery(args, api, extra);
    }

    const keySet = new Set(extractPermissionKeys(account));
    const requiredPermissions = rule.permissions?.length
      ? rule.permissions
      : rule.permission
        ? [rule.permission]
        : [];

    if (hasAnyPermission(keySet, requiredPermissions)) {
      return baseQuery(args, api, extra);
    }

    if (rule.showModal !== false) {
      maybeOpenPermissionDenied(
        api.dispatch,
        {
          requiredPermission: requiredPermissions[0],
          blockedPermissions: requiredPermissions,
          message: PERMISSION_DENIED_MESSAGE,
        },
        () => api.getState(),
      );
    }
    return deny(requiredPermissions);
  };
}
